import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { interval, Subscription } from 'rxjs';

import { DEFAULT_DASHBOARD_DISPLAY } from '../dashboards-list';

/** Interval options in seconds */
const AUTO_RELOAD_INTERVAL_S = 30;

/**
 * Shared header/layout shell for all dashboard tab pages.
 *
 * Display options (showRefresh, showAutoReload, showLastUpdated) are read
 * from the route data `display` object, falling back to defaults.
 */
@Component({
  selector: 'app-dashboard-shell',
  standalone: false,
  templateUrl: './dashboard-shell.component.html',
  styleUrls: ['./dashboard-shell.component.scss'],
})
export class DashboardShellComponent implements OnDestroy {
  /** Page heading shown in the top-left. */
  @Input() title = '';

  /** Optional description paragraph below the heading. */
  @Input() description = '';

  /** Unix timestamp (seconds) of the most recent successful data load. */
  @Input() lastReload: number | null = null;

  /** When true, a block-UI spinner overlays the content area. */
  @Input() loading = false;

  /** When false, the refresh button is not rendered. Defaults to true. */
  @Input() showRefresh = true;

  /** When true, the auto-reload toggle button is shown. Defaults to true. */
  @Input() showAutoReload = true;

  /** When true, the "Last updated" timestamp is shown. Defaults to true. */
  @Input() showLastUpdated = true;

  /** Emitted when the user clicks the refresh button or auto-reload fires. */
  @Output() refresh = new EventEmitter<void>();

  autoReload = false;
  autoReloadInterval = AUTO_RELOAD_INTERVAL_S;

  private autoReloadSub: Subscription | null = null;

  constructor(private route: ActivatedRoute) {
    const display = this.route.snapshot.data?.['display'] ?? {};
    this.showRefresh = display.showRefresh ?? DEFAULT_DASHBOARD_DISPLAY.showRefresh;
    this.showAutoReload = display.showAutoReload ?? DEFAULT_DASHBOARD_DISPLAY.showAutoReload;
    this.showLastUpdated = display.showLastUpdated ?? DEFAULT_DASHBOARD_DISPLAY.showLastUpdated;
  }

  toggleAutoReload(): void {
    this.autoReload = !this.autoReload;
    if (this.autoReload) {
      this.autoReloadSub = interval(this.autoReloadInterval * 1000).subscribe(() => {
        if (!this.loading) {
          this.refresh.emit();
        }
      });
    } else {
      this.autoReloadSub?.unsubscribe();
      this.autoReloadSub = null;
    }
  }

  ngOnDestroy(): void {
    this.autoReloadSub?.unsubscribe();
  }
}
